import React, { useState } from 'react';
import { Button, Dropdown, Modal } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import { Toaster } from '@share/utils';
import CompanyStatementDetails from './company-statement-details/component';

interface RecordType {
  accountName?: string;
  urlPdf?: string;
  creditLineInvoiceId?: string;
  creditLineInvoiceDetails?: Array<Record<string, any>>;
  [key: string]: any;
}

interface StatementActionProps {
  record: RecordType;
}

const downloadFile = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const convertToCSV = (data: RecordType): string => {
  const rows: string[] = [];

  const mainHeaders = Object.keys(data).filter((key) => key !== 'creditLineInvoiceDetails');
  rows.push(mainHeaders.join(','));
  rows.push(mainHeaders.map((header) => data[header]).join(','));

  if (data.creditLineInvoiceDetails?.length) {
    const nestedHeaders = Object.keys(data.creditLineInvoiceDetails[0]);
    rows.push('');
    rows.push(nestedHeaders.join(','));
    data.creditLineInvoiceDetails.forEach((detail) => {
      rows.push(nestedHeaders.map((header) => detail[header]).join(','));
    });
  }

  return rows.join('\n');
};

const StatementAction: React.FC<StatementActionProps> = ({ record }) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const handleAction = (actionType: string) => {
    switch (actionType) {
      case 'viewDetails':
        setIsModalVisible(true);
        break;

      case 'exportPDF':
        if (!record?.urlPdf) {
          Toaster.warning('The PDF URL is missing. Unable to download.');
          return;
        }
        downloadFile(record.urlPdf, record.urlPdf.split('/').pop() || 'document.pdf');
        break;

      case 'exportCSV':
        if (!record) {
          alert('No data available to export.');
          return;
        }
        const csvData = convertToCSV(record);
        const blob = new Blob([csvData], { type: 'text/csv' });
        const fileName = `${record.accountName || 'statement'}${
          record.creditLineInvoiceId ? `_${record.creditLineInvoiceId}` : ''
        }.csv`;
        downloadFile(URL.createObjectURL(blob), fileName);
        break;

      case 'exportHotelCSV':
        break;

      case 'exportAllPDF':
        break;

      default:
        console.error('Invalid action:', actionType);
        Toaster.error('Invalid action. Please try again.');
    }
  };

  const menuItems = [
    {
      key: 'viewDetails',
      label: 'View statement details',
      action: () => handleAction('viewDetails'),
    },
    {
      key: 'exportPDF',
      label: 'Export statement PDF',
      action: () => handleAction('exportPDF'),
      disabled: !record?.urlPdf, 
    },
    { key: 'exportCSV', label: 'Export statement CSV', action: () => handleAction('exportCSV') },
    {
      key: 'exportAllPDF',
      label: 'Export all invoices PDF',
      action: () => handleAction('exportAllPDF'),
    },
  ];

  const modalWidth = 600;
  return (
    <>
      <Dropdown
        menu={{
          items: menuItems.map((item) => ({
            key: item.key,
            label: (
              <span onClick={item.action} style={{ pointerEvents: item.disabled ? 'none' : 'auto' }}>
                {item.label}
              </span>
            ),
            disabled: item.disabled,
          })),
        }}
        trigger={['click']}
        placement="bottomRight"
        overlayStyle={{ borderRadius: '0 0 8px 8px' }}
      >
        <Button shape="circle" icon={<EllipsisOutlined />} className="circle-btn" />
      </Dropdown>
      <Modal
        title="Statement Details"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={modalWidth}
      >
        <CompanyStatementDetails record={record} />
      </Modal>
    </>
  );
};

export default StatementAction;